/**
 * Copy that renders verbatim and is never dismissible.
 *
 * These sentences are compliance surfaces, not microcopy. They live in one module
 * with a test asserting the strings match the PRD, so a layout change cannot
 * quietly paraphrase one. If a string does not fit a layout, change the layout.
 *
 * Counsel memo 2026-10-03 (docs/legal/README.md; Legal Q14, provisional pending
 * partner review, the owner authorizing provisional use): T2_BAND (B1),
 * T5_DISCLOSURE (B2), SAT7_VALUES and SAT_STANDING_DISCLAIMER (B3), T3_BOUNDARY
 * (B7), PROCESSING_TERMS (A3) and ERROR_ACK (B4) are counsel's provisional legal copy
 * (memo 2026-10-03; pending partner review), verbatim except for the conforming edits recorded in docs/legal/README.md
 * ("Open before publication"). Each is SHA-256-pinned in the test.
 *
 * Mission 6: the ANA.4 definitions below are the PRD's text verbatim
 * (docs/product/prd.md, the ANA.4 row), asserted by exact equality. That change
 * is the recorded C-003 exception (owner decision DM-01M3C8MGK4F8V0M89K52YPHJ39).
 *
 * PROCESSING_TERMS (TX.3) and ERROR_ACK (TX.4): each `version` equals the backend
 * default in backend/internal/config/config.go (`defaultProcessingTermsVersion`,
 * `defaultAckWordingVersion`), which the API validates and which prod refuses
 * while it contains `draft`. Counsel's text is provisional, so both versions keep
 * the draft marker (`2026-10-draft-v1`) and `source` names the memo; going
 * non-draft is the partner sign-off step, which the owner makes, text, pins and
 * versions in one commit. The test tells provisional counsel text (`source`) from
 * an engineering placeholder (`LEGAL_Q14_TAG`) on a draft version, and refuses a
 * non-draft version that still carries either. Paste into the typed
 * acknowledgment is allowed, for dictation and switch users (accessibility);
 * counsel's B4 finds a pasted acknowledgment adequate when the audit log records
 * the identity, the time, the sentence (its version and count) and the IP address.
 * "Furnisher" appears only in T2_BAND, T5_DISCLOSURE and PROCESSING_TERMS
 * (CLAUDE.md §3.7; counsel's D8).
 */
import { count as formatCount } from './format'

/** The provisional source of the counsel-written constants (Legal Q14). */
export const COUNSEL_MEMO = 'counsel memo 2026-10-03, provisional pending partner review'

/**
 * T.2 (counsel B1) — on results, download, transmit, and in the terms. T2Band links
 * its one "Rule Catalog" to the rule catalog.
 */
export const T2_BAND =
  'The Trellis analyzer evaluates formatting and internal consistency against designated Metro 2® ' +
  'specifications. It cannot and does not verify whether reported factual values are true or accurate. ' +
  'Trellis does not check every CRRG® requirement; view our public Rule Catalog to see what is and is not ' +
  'checked. You, the credit data reporter, are the furnisher of record for all submitted data and retain ' +
  'sole responsibility for your compliance with FCRA § 623 and applicable law.'

/** T.5 (counsel B2) — on pricing, signup, subscribe, and in the public footer. Identical on all four. */
export const T5_DISCLOSURE =
  "Trellis delivers reporting files solely under your organization's own bureau credentials. Bloom " +
  'Credit does not obtain subscriber codes for you and does not act as your liaison with the credit ' +
  'bureaus. You remain the furnisher of record for all reported data. Metering is based on generated ' +
  'records and analyzed uploaded Metro 2® records; structural validation checks use no credits.'

/** SAT.7 (counsel B3) — appended to any assistant answer that references balance, account status, payment history or DOFD values. */
export const SAT7_VALUES =
  'The analyzer checks format and consistency; it cannot verify whether these values are factual or accurate.'

/**
 * SAT.7 (counsel B3, the standing assistant disclaimer) — once, never dismissible, in the assistant
 * panel's footer. Conforming edit (U9): counsel's "Trellis AI Assistant" is "The Trellis assistant".
 */
export const SAT_STANDING_DISCLAIMER =
  'The Trellis assistant provides technical software guidance only and does not provide legal advice ' +
  'or guarantee regulatory compliance. You remain responsible for verifying data accuracy and legal compliance.'

/** ANA.4 — the severity legend. Reachable from every view that renders a severity badge. */
export const ANA4_LEGEND = {
  ERROR:
    'violates a CRRG requirement such that a bureau is expected to reject the record or file, ' +
    'or the file is internally inconsistent (e.g., trailer mismatch)',
  WARN:
    'violates a CRRG requirement or best practice that bureaus commonly accept but that ' +
    'produces inaccurate or incomplete reporting',
  INFO: 'observation, no CRRG violation',
  attribution: "Severity is Bloom's classification.",
} as const

/**
 * T.3 (counsel B7) — the claim ceiling. External copy may say this and no more.
 * Conforming edit (U9): counsel's "AI assistant engines" is "assistant engines".
 *
 * Never imply Bloom reviews files, manages bureau relationships, obtains
 * subscriber codes, or handles disputes. Trellis is inside Bloom's SOC 2 boundary
 * (CMP.1); SOC 2 statements use Compliance-approved wording only, never this constant.
 */
export const T3_BOUNDARY =
  "Trellis is powered by the same generation, validation, and assistant engines built for Bloom Credit's " +
  'enterprise furnishing platform. Trellis is a self-service software product: Bloom Credit does not ' +
  'review your data files, manage your bureau relationships, furnish data under Bloom credentials, or ' +
  'handle consumer disputes.'

/**
 * The engineering placeholder tag (the legal-copy.ts precedent); not the banned `[PLACEHOLDER]` token
 * (squad U14). No constant here carries it since counsel's memo; the test still recognises it.
 */
export const LEGAL_Q14_TAG = '[PLACEHOLDER — Legal, Q14]'

/** Counsel's A3 items, in the memo's order, each with its heading. */
const WARRANTIES = [
  'Furnisher of Record: You, the Credit Data Reporter, are the sole furnisher of record under the Fair Credit Reporting Act (FCRA) and applicable law for every file and record you transmit through Trellis.',
  'Accuracy & § 623 Ownership: You own and retain exclusive legal responsibility for the truth, accuracy, completeness, and integrity of all reported information, as well as full compliance with all obligations imposed by FCRA § 623 (15 U.S.C. § 1681s-2) and state credit reporting statutes.',
  'Authorization to Deliver: You hereby expressly authorize Bloom Credit, Inc. ("Bloom") to transmit your generated Metro 2® data files to nationwide consumer reporting agencies ("bureaus") using your organization’s own subscriber codes and SFTP credentials on your behalf.',
  'Bureau Agreement Warranty: You warrant and represent that your existing data furnisher agreements with each destination bureau expressly permit file delivery via third-party software solutions using your assigned credentials.',
  'Credential Security: You are solely responsible for maintaining the confidentiality and authorization of your bureau credentials. Bloom acts strictly as an automated transmission pipe and bears no responsibility for bureau rejections, account suspensions, or transmission failures resulting from invalid or revoked credentials.',
] as const

/**
 * TX.3 (counsel A3) — the click-through processing terms, accepted once per tenant before the first
 * transmission. Accept posts `version`; the API accepts only the configured one, so the version accepted
 * is the version rendered. The title is counsel's, in sentence case (style guide §5; a conforming edit).
 */
export const PROCESSING_TERMS = {
  version: '2026-10-draft-v1',
  source: COUNSEL_MEMO,
  title: 'Bloom Credit Trellis processing terms for bureau delivery',
  /** Counsel's five items, in the memo's order. */
  warranties: WARRANTIES,
  text: WARRANTIES.map((w, i) => `${i + 1}. ${w}`).join('\n'),
} as const

/**
 * TX.4 (counsel B4) — the typed acknowledgment of a file's ERROR findings, naming their count. Counsel kept
 * the sentences and allowed paste (B4); the version stays a draft until the partner signs off.
 */
export const ERROR_ACK = {
  version: '2026-10-draft-v1',
  source: COUNSEL_MEMO,
  singular: 'I understand this file has {count} ERROR finding and I want to transmit it anyway.',
  plural: 'I understand this file has {count} ERROR findings and I want to transmit it anyway.',
} as const

/** The sentence the reporter types: the count formatted as `format.count` renders it, singular at 1. */
export function errorAcknowledgmentSentence(count: number): string {
  return (count === 1 ? ERROR_ACK.singular : ERROR_ACK.plural).replace('{count}', formatCount(count))
}
